/**
 * The printed incident report — a formal sheriff's office document on letter paper: letterhead with a
 * seal, the report number boxed, every section as labeled fields in a grid, the narrative, approved
 * supplements, signature lines, and on every page "Page X of Y" plus a confidentiality footer
 * (later pages also carry a running header). Black and white friendly. Opened from the report's
 * Print button in its own tab, without the app around it.
 */
import { createContext, Fragment, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ARREST_TYPES,
  ETHNICITIES,
  EXCEPTIONAL_CLEARANCE,
  INJURIES,
  LOCATION_TYPES,
  LOSS_TYPES,
  NARCAN_GIVERS,
  OFFENSE_BY_CODE,
  PROPERTY_TYPES,
  RACES,
  RELATIONSHIPS,
  RESIDENT_STATUS,
  SEXES,
  VEHICLE_PROPERTY,
  VICTIM_TYPES,
  WEAPONS,
  juvenilesIn,
  labelOf,
  type IncidentReport,
  type ReportPerson,
  type ReportProperty,
} from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDate, fmtDateTime } from '../shared/format.ts';
import { ErrorBox, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const dash = (v: unknown) => (v == null || v === '' ? '—' : String(v));
const dt = (v: string | null | undefined) => (v ? fmtDateTime(v).replace(/^\w+, /, '') : '—');
const money = (n: number | null | undefined) => (n == null ? '—' : `$${n.toLocaleString('en-US')}`);
const height = (inches: number | null) => (inches ? `${Math.floor(inches / 12)}′ ${inches % 12}″` : null);
const STATUS: Record<string, string> = { draft: 'Draft — not submitted', submitted: 'Awaiting supervisor approval', returned: 'Returned for correction', approved: 'Approved' };
const ROLE_ORDER = ['victim', 'complainant', 'suspect', 'arrestee', 'witness', 'other'] as const;
const ROLE_LABEL: Record<string, string> = { victim: 'Victim', complainant: 'Complainant', suspect: 'Suspect', arrestee: 'Arrestee', witness: 'Witness', other: 'Other person' };

function nameOf(p: ReportPerson) {
  const parts = [p.first, p.middle, p.last, p.suffix].filter(Boolean).join(' ');
  return p.name || parts || (p.unknown ? 'Unknown' : '—');
}
function ageOf(p: ReportPerson, on: string | null) {
  if (p.dob) {
    const d = new Date(`${p.dob}T12:00:00Z`);
    const ref = on ? new Date(on) : new Date();
    let a = ref.getUTCFullYear() - d.getUTCFullYear();
    if (ref.getUTCMonth() < d.getUTCMonth() || (ref.getUTCMonth() === d.getUTCMonth() && ref.getUTCDate() < d.getUTCDate())) a--;
    return String(a);
  }
  return p.age ?? null;
}
function clearanceText(c: IncidentReport['clearance']) {
  if (c.status === 'open') return 'Open';
  if (c.status === 'arrest') return 'Cleared by arrest';
  if (c.status === 'exceptional') return `Cleared exceptionally — ${labelOf(EXCEPTIONAL_CLEARANCE, c.reason)}`;
  if (c.status === 'closed') return 'Closed, not cleared';
  return 'Unfounded';
}

// ─── Redaction ──────────────────────────────────────────────────────────────
type Hide = 'victim' | 'juvenile' | 'pio';
interface Red {
  hide: Map<string, Hide>;
  terms: string[];
  spots: Set<string>;
  edit: boolean;
  togglePerson: (id: string) => void;
  toggleSpot: (key: string) => void;
}
const NO_RED: Red = { hide: new Map(), terms: [], spots: new Set(), edit: false, togglePerson: () => undefined, toggleSpot: () => undefined };
const RedCtx = createContext<Red>(NO_RED);
const esc = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Victims who are people (a business or the government isn't redacted). */
const isPersonVictim = (p: ReportPerson) => p.roles.includes('victim') && (!p.type || p.type === 'I' || p.type === 'L');
/** A black bar where something was taken out. The words themselves are never on the page. */
function Bar({ label, n = 10 }: { label?: string; n?: number }) {
  return label ? <span className="irp-red lbl">{label}</span> : <span className="irp-red">{'█'.repeat(Math.max(3, Math.min(n, 30)))}</span>;
}
/** Words of a person's name, for blacking them out of the narrative too. */
export function nameTerms(p: ReportPerson): string[] {
  const parts = [p.name, p.first, p.middle, p.last, [p.first, p.last].filter(Boolean).join(' ')].filter(Boolean) as string[];
  const words = parts.flatMap((x) => [x, ...x.split(/\s+/)]).map((x) => x.replace(/[.,]/g, '').trim()).filter((x) => x.length >= 2 && !/^(jr|sr|ii|iii|iv|unknown|male|female|white|black)$/i.test(x));
  return [...new Set(words)];
}
/** Text with redactions applied; in PIO redact mode, click any word to black it out (or bring it back). */
function RedText({ text, section, clickable = true }: { text: string; section: string; clickable?: boolean }) {
  const red = useContext(RedCtx);
  const ranges: [number, number][] = [];
  for (const t of red.terms) {
    const term = t.trim();
    if (term.length < 2) continue;
    const re = new RegExp(`(^|[^\\p{L}\\p{N}])(${esc(term)})(?=$|[^\\p{L}\\p{N}])`, 'giu');
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const st = m.index + m[1]!.length;
      ranges.push([st, st + m[2]!.length]);
      re.lastIndex = st + m[2]!.length;
    }
  }
  const out: ReactNode[] = [];
  let pos = 0;
  for (const m of text.matchAll(/\S+/g)) {
    const st = m.index!;
    const w = m[0];
    out.push(text.slice(pos, st));
    const key = `${section}:${st}`;
    const byTerm = ranges.some(([a, b]) => st < b && st + w.length > a);
    const bySpot = red.spots.has(key);
    if (red.edit && clickable && !byTerm)
      out.push(
        <button key={key} type="button" className={`irp-tk${bySpot ? ' irp-red' : ''}`} onClick={() => red.toggleSpot(key)} aria-pressed={bySpot} aria-label={bySpot ? 'Redacted word — click to bring it back' : `Redact “${w}”`}>
          {bySpot ? '█'.repeat(w.length) : w}
        </button>,
      );
    else out.push(byTerm || bySpot ? <span key={key} className="irp-red">{'█'.repeat(w.length)}</span> : w);
    pos = st + w.length;
  }
  out.push(text.slice(pos));
  return <>{out}</>;
}

/** Table text, with "black out everywhere" words applied. */
const T = (x: string | null | undefined) => (x ? <RedText text={x} section="t" clickable={false} /> : '—');

/** One labeled box in a field grid. `span` = how many of the 6 columns it takes. */
function F({ label, children, span = 1 }: { label: string; children: ReactNode; span?: number }) {
  return (
    <div className="irp-f" style={{ gridColumn: `span ${span}` }}>
      <span className="irp-l">{label}</span>
      <span className="irp-v">{children === '' || children == null ? '—' : typeof children === 'string' ? <RedText text={children} section="f" clickable={false} /> : children}</span>
    </div>
  );
}
function Section({ n, title, children, keep }: { n: number | string; title: string; children: ReactNode; keep?: boolean }) {
  return (
    <section className={`irp-sec${keep ? ' keep' : ''}`}>
      <h2 className="irp-h">
        <span className="irp-n">{n}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function PersonBlock({ p, all, occurred, k }: { p: ReportPerson; all: ReportPerson[]; occurred: string | null; k: string }) {
  const red = useContext(RedCtx);
  const hidden = red.hide.get(p.id);
  const roles = p.roles.map((r) => ROLE_LABEL[r] ?? r).join(' / ');
  const rel = Object.entries(p.relationships ?? {})
    .map(([sid, code]) => {
      const s = all.find((x) => x.id === sid);
      return `${labelOf(RELATIONSHIPS, code)}${s ? ` (to ${red.hide.has(s.id) ? 'a person redacted on this copy' : nameOf(s)})` : ''}`;
    })
    .join('; ');
  const unk = (t: string) => p.unknownTraits?.includes(t);
  const juv = hidden === 'juvenile';
  /** Hidden for this person: the bar; otherwise the value. */
  const H = (v: ReactNode, n?: number) => (hidden ? <Bar n={n} /> : v);
  return (
    <div className={`irp-person${hidden ? ' hidden' : ''}`}>
      <div className="irp-ptag">
        <span>{k}</span>
        {roles}
        {hidden && <em className="irp-redtag">{juv ? 'Juvenile — details not printed' : 'Redacted on this copy'}</em>}
        {red.edit && (
          <label className="irp-redpick no-print">
            <input type="checkbox" checked={!!hidden} disabled={hidden === 'victim' || hidden === 'juvenile'} onChange={() => red.togglePerson(p.id)} /> Redact this person
          </label>
        )}
      </div>
      <div className="irp-grid">
        <F label="Name (last, first, middle)" span={3}>
          {hidden ? <Bar label={juv ? 'JUVENILE' : 'REDACTED'} /> : <strong>{p.last ? [p.last, [p.first, p.middle, p.suffix].filter(Boolean).join(' ')].filter(Boolean).join(', ') : nameOf(p)}</strong>}
        </F>
        <F label="Date of birth">{H(p.dob ? fmtDate(p.dob) : '—', 8)}</F>
        <F label="Age">{ageOf(p, occurred)}</F>
        <F label="Sex">{p.sex ? labelOf(SEXES, p.sex) : ''}</F>
        <F label="Race">{p.race ? labelOf(RACES, p.race) : ''}</F>
        <F label="Ethnicity">{p.ethnicity ? labelOf(ETHNICITIES, p.ethnicity) : ''}</F>
        <F label="Height">{juv ? <Bar n={4} /> : unk('height') ? 'Unknown' : height(p.height)}</F>
        <F label="Weight">{juv ? <Bar n={4} /> : unk('weight') ? 'Unknown' : p.weight ? `${p.weight} lb` : ''}</F>
        <F label="Hair">{juv ? <Bar n={4} /> : unk('hair') ? 'Unknown' : p.hair}</F>
        <F label="Eyes">{juv ? <Bar n={4} /> : unk('eyes') ? 'Unknown' : p.eyes}</F>
        <F label="Address" span={3}>
          {p.address ? H(p.address, 24) : ''}
        </F>
        <F label="Phone">{p.phone ? H(p.phone, 10) : ''}</F>
        <F label="Resident status" span={2}>
          {p.resident ? labelOf(RESIDENT_STATUS, p.resident) : ''}
        </F>
        {p.roles.includes('victim') && (
          <>
            <F label="Victim type" span={2}>
              {p.type ? labelOf(VICTIM_TYPES, p.type) : ''}
            </F>
            <F label="Injuries" span={2}>
              {(p.injuries ?? []).map((i) => labelOf(INJURIES, i)).join(', ')}
            </F>
            <F label="Relationship to suspect" span={2}>
              {rel}
            </F>
          </>
        )}
        {(p.marks || p.description) && (
          <F label="Scars, marks, clothing, description" span={6}>
            {hidden ? <Bar n={20} /> : <RedText text={[p.marks, p.description].filter(Boolean).join(' · ')} section={`d-${p.id.slice(0, 12)}`} />}
          </F>
        )}
        {p.arrest && (
          <>
            <F label="Arrested" span={2}>
              {dt(p.arrest.at)}
            </F>
            <F label="Arrest type" span={2}>
              {p.arrest.type ? labelOf(ARREST_TYPES, p.arrest.type) : ''}
            </F>
            <F label="Booking no.">{p.arrest.bookingNumber ? H(p.arrest.bookingNumber, 8) : ''}</F>
            <F label="Warrant no.">{p.arrest.warrant ? H(p.arrest.warrant, 8) : ''}</F>
            <F label="Charges" span={6}>
              {p.arrest.localCharges?.length
                ? p.arrest.localCharges.map((c) => `${c.statute} ${c.description}${c.warrant ? ` (warrant ${c.warrant})` : ''}`).join('; ')
                : p.arrest.charge
                  ? OFFENSE_BY_CODE.get(p.arrest.charge)?.plain ?? p.arrest.charge
                  : ''}
            </F>
          </>
        )}
      </div>
    </div>
  );
}

function VehicleTable({ items }: { items: ReportProperty[] }) {
  return (
    <table className="irp-t">
      <thead>
        <tr>
          <th>#</th>
          <th>Year / make / model</th>
          <th>Color</th>
          <th>Tag</th>
          <th>VIN</th>
          <th>Involvement</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        {items.map((v, i) => (
          <tr key={v.id}>
            <td>V{i + 1}</td>
            <td>
              <strong>{T([v.year, v.make, v.model].filter(Boolean).join(' ') || labelOf(PROPERTY_TYPES, v.type))}</strong>
              {v.description && <div className="irp-sub">{T(v.description)}</div>}
            </td>
            <td>{T(v.color)}</td>
            <td>{T(v.plate)}</td>
            <td className="irp-mono">{T(v.vin)}</td>
            <td>{labelOf(LOSS_TYPES, v.loss)}</td>
            <td>{v.valueUnknown ? 'Unknown' : money(v.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function PropertyTable({ items, evidence }: { items: ReportProperty[]; evidence: any[] }) {
  return (
    <table className="irp-t">
      <thead>
        <tr>
          <th>#</th>
          <th>Item</th>
          <th>Make / model</th>
          <th>Serial no.</th>
          <th>Qty</th>
          <th>Status</th>
          <th className="r">Value</th>
        </tr>
      </thead>
      <tbody>
        {items.map((x, i) => {
          const ev = evidence.find((e) => e.item_ref === x.id);
          return (
            <tr key={x.id}>
              <td>P{i + 1}</td>
              <td>
                <strong>{T(x.description || labelOf(PROPERTY_TYPES, x.type))}</strong>
                <div className="irp-sub">
                  {labelOf(PROPERTY_TYPES, x.type)}
                  {ev ? ` · Evidence ${ev.number}` : x.evidence ? ' · Evidence' : ''}
                  {x.recoveredOn ? ` · Recovered ${fmtDate(x.recoveredOn)}` : ''}
                </div>
              </td>
              <td>{T([x.make, x.model].filter(Boolean).join(' '))}</td>
              <td className="irp-mono">{T(x.serial)}</td>
              <td>{dash(x.quantity)}</td>
              <td>{labelOf(LOSS_TYPES, x.loss)}</td>
              <td className="r">{x.valueUnknown ? 'Unknown' : money(x.value)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ReportPrint({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/reports/${id}`), [id]);
  const { me, can } = useAuth();
  const isPio = can('pio.write') || can('pio.approve');
  const saved = useLoad(() => (isPio ? api.get<any>(`/api/reports/${id}/redaction`) : Promise.resolve({ redaction: null })), [id, isPio]);
  const [answered, setAnswered] = useState(false);
  const [victims, setVictims] = useState<'redact' | 'show' | null>(null);
  const [juveniles, setJuveniles] = useState<'print' | 'withhold' | null>(null);
  const [usePio, setUsePio] = useState(true);
  const [edit, setEdit] = useState(false);
  const [pioPeople, setPioPeople] = useState<Set<string>>(new Set());
  const [pioTerms, setPioTerms] = useState<string[]>([]);
  const [spots, setSpots] = useState<Set<string>>(new Set());
  const [term, setTerm] = useState('');
  const [dirty, setDirty] = useState(false);
  const sv = useAction();
  useEffect(() => {
    const x = saved.data?.redaction;
    if (x) {
      setPioPeople(new Set(x.people));
      setPioTerms(x.terms);
      setSpots(new Set(x.spots));
    }
  }, [saved.data]);
  const all = useMemo(() => {
    const d = r.data;
    if (!d) return [] as ReportPerson[];
    return [...(d.report?.people ?? []), ...(d.supplements ?? []).filter((s: any) => s.status === 'approved').flatMap((s: any) => s.body?.people ?? [])] as ReportPerson[];
  }, [r.data]);
  const kids = useMemo(() => (r.data ? juvenilesIn(all, r.data.report?.occurredFrom ? new Date(r.data.report.occurredFrom) : new Date()) : []), [all, r.data]);
  const red: Red = useMemo(() => {
    const hide = new Map<string, Hide>();
    if (victims === 'redact') for (const p of all) if (isPersonVictim(p)) hide.set(p.id, 'victim');
    if (juveniles === 'withhold') for (const k of kids) hide.set(k.id, 'juvenile');
    if (usePio || edit) for (const pid of pioPeople) if (!hide.has(pid)) hide.set(pid, 'pio');
    const terms = [...new Set([...all.filter((p) => hide.has(p.id)).flatMap(nameTerms), ...(usePio || edit ? pioTerms : [])])];
    return {
      hide,
      terms,
      spots: usePio || edit ? spots : new Set<string>(),
      edit,
      togglePerson: (pid) => (setPioPeople((s0) => { const s1 = new Set(s0); s1.has(pid) ? s1.delete(pid) : s1.add(pid); return s1; }), setDirty(true)),
      toggleSpot: (k) => (setSpots((s0) => { const s1 = new Set(s0); s1.has(k) ? s1.delete(k) : s1.add(k); return s1; }), setDirty(true)),
    };
  }, [victims, juveniles, usePio, edit, pioPeople, pioTerms, spots, all, kids]);
  const redacted = red.hide.size > 0 || red.spots.size > 0 || (usePio && pioTerms.length > 0);
  // Every print is logged with what was redacted.
  useEffect(() => {
    if (!answered) return;
    const log = () => void api.post(`/api/reports/${id}/printed`, { victims: victims === 'redact' ? 'redacted' : 'shown', juveniles: kids.length ? (juveniles === 'withhold' ? 'withheld' : 'printed') : null, pio: usePio && (pioPeople.size + pioTerms.length + spots.size > 0) }).catch(() => undefined);
    window.addEventListener('beforeprint', log);
    return () => window.removeEventListener('beforeprint', log);
  }, [answered, victims, juveniles, usePio, pioPeople, pioTerms, spots, kids, id]);
  const data = r.data;
  const rep: IncidentReport | undefined = data?.report;
  const ag = data?.agency ?? { name: me?.agency.name ?? '', county: null, address: null, phone: null };
  const caseNo = data?.incident.caseNumber ?? '';
  // Margin boxes need literal strings: the running header and footers are written per report.
  const q = (s: string) => `"${s.replace(/["\\]/g, '\\$&').replace(/\n/g, ' ')}"`;
  const pageCss = data
    ? `@page { size: letter; margin: 0.6in 0.55in 0.7in;
  @top-left { content: ${q(`${ag.name} — Incident Report ${caseNo}`)}; font: 600 8pt 'Public Sans', Arial, sans-serif; color: #000; vertical-align: bottom; padding-bottom: 6pt; }
  @top-right { content: ${q(`Case ${caseNo}`)}; font: 600 8pt 'Public Sans', Arial, sans-serif; color: #000; vertical-align: bottom; padding-bottom: 6pt; }
  @bottom-left { content: "CONFIDENTIAL — LAW ENFORCEMENT SENSITIVE. Not for release without approval of the Sheriff or the Solicitor. Contains personal information protected by law."; font: 7pt 'Public Sans', Arial, sans-serif; color: #000; vertical-align: top; padding-top: 6pt; width: 75%; }
  @bottom-right { content: "Page " counter(page) " of " counter(pages); font: 600 8pt 'Public Sans', Arial, sans-serif; color: #000; vertical-align: top; padding-top: 6pt; }
}
@page :first { @top-left { content: none; } @top-right { content: none; } }`
    : '';
  useEffect(() => {
    if (data) document.title = `Incident Report ${caseNo}`;
  }, [data, caseNo]);
  if (r.error) return <ErrorBox error={r.error} />;
  if (!data || !rep) return <Spinner />;
  const hasVictims = all.some((p) => isPersonVictim(p));
  const savedBy = saved.data?.redaction;
  if (!answered)
    return (
      <div className="irp-wrap">
        <div className="irp-ask card" role="dialog" aria-labelledby="irp-ask-h">
          <h1 id="irp-ask-h">Before you print report {data.incident.caseNumber}</h1>
          {hasVictims && (
            <fieldset>
              <legend>Redact victim information on this copy?</legend>
              <p className="small muted">Victims’ names, dates of birth, addresses and phone numbers are blacked out, and their names are blacked out in the narrative.</p>
              <div className="row gap-sm">
                <button type="button" className={`btn${victims === 'redact' ? ' primary' : ''}`} aria-pressed={victims === 'redact'} onClick={() => setVictims('redact')}>
                  Yes, redact victims
                </button>
                <button type="button" className={`btn${victims === 'show' ? ' primary' : ''}`} aria-pressed={victims === 'show'} onClick={() => setVictims('show')}>
                  No, print it all
                </button>
              </div>
            </fieldset>
          )}
          {kids.length > 0 && (
            <fieldset>
              <legend>
                This report has {kids.length === 1 ? 'a juvenile' : `${kids.length} juveniles`} ({kids.map((k) => `${k.roles.map((x) => ROLE_LABEL[x]?.toLowerCase() ?? x).join('/')}, age ${k.age}`).join('; ')}). Print juvenile details?
              </legend>
              <div className="row gap-sm">
                <button type="button" className={`btn${juveniles === 'withhold' ? ' primary' : ''}`} aria-pressed={juveniles === 'withhold'} onClick={() => setJuveniles('withhold')}>
                  No, leave them off
                </button>
                <button type="button" className={`btn${juveniles === 'print' ? ' primary' : ''}`} aria-pressed={juveniles === 'print'} onClick={() => setJuveniles('print')}>
                  Yes, print juvenile details
                </button>
              </div>
            </fieldset>
          )}
          {isPio && savedBy && (
            <label className="check">
              <input type="checkbox" checked={usePio} onChange={(e) => setUsePio(e.target.checked)} /> Use the PIO redactions saved by {savedBy.by_name} ({fmtDateTime(savedBy.updated_at)})
            </label>
          )}
          <div className="row gap-sm">
            <button type="button" className="btn primary" disabled={(hasVictims && !victims) || (kids.length > 0 && !juveniles)} onClick={() => setAnswered(true)}>
              Continue
            </button>
            {isPio && (
              <button type="button" className="btn" disabled={(hasVictims && !victims) || (kids.length > 0 && !juveniles)} onClick={() => (setAnswered(true), setEdit(true), setUsePio(true))}>
                Continue and redact by hand (PIO)
              </button>
            )}
            <button type="button" className="btn link" onClick={() => history.back()}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );

  const people = [...rep.people].sort((a, b) => ROLE_ORDER.indexOf(a.roles[0] as any) - ROLE_ORDER.indexOf(b.roles[0] as any));
  const vehicles = rep.property.filter((p) => VEHICLE_PROPERTY.includes(p.type));
  const property = rep.property.filter((p) => !VEHICLE_PROPERTY.includes(p.type));
  const evidence: any[] = data.evidence ?? [];
  const sups: any[] = (data.supplements ?? []).filter((s: any) => s.status === 'approved');
  const totalLoss = property.filter((p) => p.loss === '7').reduce((s, p) => s + (p.value ?? 0), 0);
  const inc = data.incident;
  let n = 0;
  const next = () => ++n;
  const pk = (p: ReportPerson) => {
    const i = people.indexOf(p);
    const role = p.roles[0];
    const k = people.filter((x, j) => j <= i && x.roles[0] === role).length;
    return `${(role ?? 'o')[0]!.toUpperCase()}${k}`;
  };

  return (
    <div className="irp-wrap">
      <style>{pageCss}</style>
      <div className="irp-tools no-print">
        <button type="button" className="btn" onClick={() => history.back()}>
          ← Back
        </button>
        <span className="small muted">
          {redacted ? 'Redacted copy. ' : ''}Letter size, black and white; page numbers and the confidentiality notice go on every page.
        </span>
        <button type="button" className="btn" onClick={() => (setAnswered(false), setEdit(false))}>
          Change what’s redacted
        </button>
        {isPio && (
          <button type="button" className={`btn${edit ? ' primary' : ''}`} onClick={() => setEdit(!edit)}>
            {edit ? 'Done redacting' : 'Redact (PIO)'}
          </button>
        )}
        <button type="button" className="btn primary" disabled={edit} onClick={() => window.print()}>
          Print
        </button>
      </div>
      {edit && (
        <div className="irp-pio no-print">
          <strong>PIO redaction.</strong> Click any word in the narrative to black it out (click again to bring it back), check “Redact this person” on anyone, or black out a word or phrase everywhere it appears:
          <form
            className="row gap-sm wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (term.trim().length >= 2) setPioTerms([...new Set([...pioTerms, term.trim()])]), setTerm(''), setDirty(true);
            }}
          >
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="e.g. a street name, a business, a plate" aria-label="Word or phrase to black out everywhere" maxLength={120} />
            <button className="btn small">Black it out everywhere</button>
          </form>
          {pioTerms.length > 0 && (
            <div className="row gap-sm wrap">
              {pioTerms.map((t) => (
                <button key={t} type="button" className="btn small" onClick={() => (setPioTerms(pioTerms.filter((x) => x !== t)), setDirty(true))} aria-label={`Stop blacking out “${t}”`}>
                  “{t}” ✕
                </button>
              ))}
            </div>
          )}
          <div className="row gap-sm">
            <button
              type="button"
              className="btn primary small"
              disabled={sv.busy || !dirty}
              onClick={() => sv.run(async () => (await api.post(`/api/reports/${id}/redaction`, { people: [...pioPeople], terms: pioTerms, spots: [...spots] }), setDirty(false), saved.reload()))}
            >
              {dirty ? 'Save these redactions' : 'Saved'}
            </button>
            <span className="small muted">Saved redactions are there next time anyone in the PIO office prints this report.</span>
          </div>
          <ErrorBox error={sv.error} />
        </div>
      )}
      <RedCtx.Provider value={red}>
      <article className={`irp${edit ? ' editing' : ''}`} aria-label={`Incident report ${caseNo}`}>
        <header className="irp-head">
          <div className="irp-seal" aria-label="Agency seal">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              <circle cx="50" cy="50" r="46" fill="none" stroke="#000" strokeWidth="2" />
              <circle cx="50" cy="50" r="38" fill="none" stroke="#000" strokeWidth="0.8" strokeDasharray="2 2" />
              <path d="M50 26 l17 7 v13 c0 11-7 18-17 21-10-3-17-10-17-21 V33z" fill="none" stroke="#000" strokeWidth="2" />
              <text x="50" y="84" textAnchor="middle" fontSize="7" fontFamily="Arial" letterSpacing="1">SEAL</text>
            </svg>
          </div>
          <div className="irp-agency">
            <div className="irp-aname">{ag.name}</div>
            {ag.county && <div>{ag.county}</div>}
            <div>{[ag.address, ag.phone].filter(Boolean).join(' · ') || ' '}</div>
            <h1>Incident Report</h1>
          </div>
          <dl className="irp-box">
            <div className="big">
              <dt>Case / report no.</dt>
              <dd>{caseNo}</dd>
            </div>
            <div>
              <dt>Report status</dt>
              <dd>{STATUS[inc.status] ?? inc.status}</dd>
            </div>
            <div>
              <dt>Case status</dt>
              <dd>{clearanceText(rep.clearance)}</dd>
            </div>
            <div>
              <dt>Related / CAD</dt>
              <dd>{dash(rep.related)}</dd>
            </div>
            {redacted && (
              <div className="irp-redcopy">
                <dt>Copy</dt>
                <dd>REDACTED</dd>
              </div>
            )}
          </dl>
        </header>

        <Section n={next()} title="Incident" keep>
          <div className="irp-grid">
            <F label="Occurred from" span={2}>
              {rep.occurredUnknown === 'date' ? 'Date unknown' : dt(rep.occurredFrom)}
            </F>
            <F label="Occurred to" span={2}>
              {dt(rep.occurredTo)}
            </F>
            <F label="Reported" span={2}>
              {dt(rep.reportedAt)}
            </F>
            <F label="Location of incident" span={4}>
              <strong>{T([rep.location.line1, rep.location.city, [rep.location.state, rep.location.zip].filter(Boolean).join(' ')].filter(Boolean).join(', '))}</strong>
            </F>
            <F label="Location type" span={2}>
              {rep.location.type ? labelOf(LOCATION_TYPES, rep.location.type) : ''}
            </F>
            {rep.location.detail && (
              <F label="Location detail" span={6}>
                {rep.location.detail}
              </F>
            )}
            {rep.offenses.some((o: any) => o.code === 'NC-INFO') && (
              <F label="Report type" span={2}>
                INFORMATION ONLY — no crime, no charges
              </F>
            )}
            {rep.societyVictim && (
              <F label="Victim" span={2}>
                Society / the public
              </F>
            )}
            {rep.people.some((p: any) => p.unknown && p.roles?.includes('suspect')) && (
              <F label="Offender" span={2}>
                Unknown
              </F>
            )}
            {rep.narcan?.used && (
              <F label="Narcan (naloxone)" span={6}>
                Used{rep.narcan.doses ? ` — ${rep.narcan.doses} dose${rep.narcan.doses === 1 ? '' : 's'}` : ''}; given by: {NARCAN_GIVERS.find((g) => g.code === rep.narcan!.givenBy)?.label ?? '—'}
                {rep.narcan.givenBy === 'me' ? (rep.narcan.formDone ? '; Narcan use form completed' : '; Narcan use form NOT completed') : ''}
              </F>
            )}
          </div>
        </Section>

        <Section n={next()} title="Offenses" keep>
          <table className="irp-t">
            <thead>
              <tr>
                <th>#</th>
                <th>Offense</th>
                <th>Code</th>
                <th>Completed</th>
                <th>Weapon / force</th>
                <th>Entry</th>
              </tr>
            </thead>
            <tbody>
              {rep.offenses.map((o, i) => (
                <tr key={o.id}>
                  <td>{i + 1}</td>
                  <td>
                    <strong>{OFFENSE_BY_CODE.get(o.code)?.plain ?? o.code}</strong>
                  </td>
                  <td className="irp-mono">{o.code}</td>
                  <td>{o.attempted == null ? '—' : o.attempted ? 'Attempted' : 'Completed'}</td>
                  <td>{o.weapons.map((w) => labelOf(WEAPONS, w)).join(', ') || '—'}</td>
                  <td>{o.entry === 'F' ? 'Forced' : o.entry === 'N' ? 'No force' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {people.length > 0 && (
          <Section n={next()} title={`People (${people.length})`}>
            {people.map((p) => (
              <PersonBlock key={p.id} p={p} all={rep.people} occurred={rep.occurredFrom} k={pk(p)} />
            ))}
          </Section>
        )}

        {vehicles.length > 0 && (
          <Section n={next()} title="Vehicles" keep>
            <VehicleTable items={vehicles} />
          </Section>
        )}

        {property.length > 0 && (
          <Section n={next()} title="Property" keep>
            <PropertyTable items={property} evidence={evidence} />
            {totalLoss > 0 && <div className="irp-total">Total stolen: {money(totalLoss)}</div>}
          </Section>
        )}

        {evidence.length > 0 && (
          <Section n={next()} title="Evidence" keep>
            <table className="irp-t">
              <thead>
                <tr>
                  <th>Evidence no.</th>
                  <th>Description</th>
                  <th>Where it is</th>
                </tr>
              </thead>
              <tbody>
                {evidence.map((e) => (
                  <tr key={e.id}>
                    <td className="irp-mono">{e.number}</td>
                    <td>{T(e.description)}</td>
                    <td>{e.location || (e.status === 'pending' ? 'Not yet checked in' : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        <Section n={next()} title="Narrative">
          <div className="irp-narr">{rep.narrative ? <RedText text={rep.narrative} section="n" /> : '—'}</div>
        </Section>

        {sups.map((s) => (
          <Section key={s.id} n={next()} title={`Supplement ${s.number}`}>
            <div className="irp-grid">
              <F label="Written by" span={2}>
                {s.author_name}
              </F>
              <F label="Submitted" span={2}>
                {dt(s.submitted_at)}
              </F>
              <F label="Approved" span={2}>
                {dt(s.approved_at)}
              </F>
            </div>
            <div className="irp-narr">{s.body?.narrative ? <RedText text={s.body.narrative} section={`s${s.number}`} /> : '—'}</div>
            {s.body?.people?.length > 0 && s.body.people.map((p: ReportPerson, i: number) => <PersonBlock key={p.id} p={p} all={s.body.people} occurred={rep.occurredFrom} k={`S${s.number}-${i + 1}`} />)}
            {s.body?.property?.length > 0 && <PropertyTable items={s.body.property} evidence={[]} />}
            {s.body?.clearance && <p className="irp-sub">Case status changed to: {clearanceText(s.body.clearance)}</p>}
          </Section>
        ))}

        <Section n={next()} title="Approval" keep>
          <div className="irp-sigs">
            <div className="irp-sig">
              <div className="irp-sigline">{inc.submittedAt ? <span className="irp-esig">Electronically signed {dt(inc.submittedAt)}</span> : null}</div>
              <div className="irp-signame">
                <strong>{inc.authorName}</strong>
              </div>
              <div className="irp-l">Reporting officer · signature and date</div>
            </div>
            <div className="irp-sig">
              <div className="irp-sigline">{inc.approvedAt ? <span className="irp-esig">Electronically approved {dt(inc.approvedAt)}</span> : null}</div>
              <div className="irp-signame">
                <strong>{inc.approvedBy ?? ' '}</strong>
              </div>
              <div className="irp-l">Approving supervisor · signature and date</div>
            </div>
          </div>
        </Section>

        {data.events?.length > 0 && (
          <Section n={next()} title="Report history" keep>
            <ul className="irp-hist">
              {data.events.map((e: any, i: number) => (
                <Fragment key={i}>
                  <li>
                    <span className="irp-when">{dt(e.at)}</span>
                    {e.action} — {e.by_name}
                    {e.note ? `: “${e.note}”` : ''}
                  </li>
                </Fragment>
              ))}
            </ul>
          </Section>
        )}
        <p className="irp-printed">
          Printed {fmtDateTime(new Date())} by {me?.user.displayName ?? '—'}. The approved report in BlueHQ is the official record; supplements are added after approval and do not change it.
        </p>
      </article>
      </RedCtx.Provider>
    </div>
  );
}
